import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  LinearProgress,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Paper,
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Assignment,
  Person,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { 
  assessmentAPI, 
  AssessmentPortalData, 
  PatientAssessment,
  AssessmentTemplate
} from '../services/assessmentService';
import PHQ9Assessment from '../components/assessment/PHQ9Assessment';
import GAD7Assessment from '../components/assessment/GAD7Assessment';
import BiopsychosocialAssessment from '../components/assessment/BiopsychosocialAssessment';
import DocumentUploadAssessment from '../components/assessment/DocumentUploadAssessment';

const AssessmentPortalPage: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [portalData, setPortalData] = useState<AssessmentPortalData | null>(null);
  const [currentAssessment, setCurrentAssessment] = useState<PatientAssessment | null>(null);
  const [currentTemplate, setCurrentTemplate] = useState<AssessmentTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completionStatus, setCompletionStatus] = useState({
    completed: 0,
    total: 0,
    percentage: 0
  });

  useEffect(() => {
    if (appointmentId) {
      loadPortalData();
    }
  }, [appointmentId]);

  const loadPortalData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await assessmentAPI.getAssessmentPortal(appointmentId!);
      setPortalData(response.data);

      // Calculate completion status
      const completed = response.data.assessments.filter(a => a.status === 'completed').length;
      const total = response.data.assessments.length;
      const percentage = total > 0 ? (completed / total) * 100 : 0;

      setCompletionStatus({ completed, total, percentage });

      // Auto-select first incomplete assessment
      const incompleteAssessment = response.data.assessments.find(a => a.status !== 'completed');
      if (incompleteAssessment) {
        await selectAssessment(incompleteAssessment.assessmentId);
      }

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load assessment portal');
    } finally {
      setLoading(false);
    }
  };

  const selectAssessment = async (assessmentId: string) => {
    try {
      const assessment = portalData?.assessments.find(a => a.assessmentId === assessmentId);
      if (!assessment) return;

      const templateResponse = await assessmentAPI.getAssessmentTemplate(assessmentId);
      
      setCurrentAssessment(assessment);
      setCurrentTemplate(templateResponse.data);

      // Track access
      await assessmentAPI.trackAccess(assessmentId);
    } catch (err) {
      setError('Failed to load assessment details');
    }
  };

  const handleAssessmentComplete = async () => {
    // Reload portal data to update completion status
    await loadPortalData();
    
    // Check if all assessments are complete
    if (portalData && completionStatus.completed + 1 >= completionStatus.total) {
      // All assessments complete - show success message
      setCurrentAssessment(null);
      setCurrentTemplate(null);
    } else {
      // Move to next incomplete assessment
      const nextIncomplete = portalData?.assessments.find(a => 
        a.status !== 'completed' && a.assessmentId !== currentAssessment?.assessmentId
      );
      if (nextIncomplete) {
        await selectAssessment(nextIncomplete.assessmentId);
      }
    }
  };

  const formatAppointmentDate = (dateTime: string) => {
    const date = new Date(dateTime);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getAssessmentIcon = (templateId: string) => {
    switch (templateId) {
      case 'phq9': return '📋';
      case 'gad7': return '🧠';  
      case 'biopsychosocial': return '👤';
      case 'document_upload': return '📎';
      default: return '📝';
    }
  };

  const getAssessmentName = (templateId: string) => {
    switch (templateId) {
      case 'phq9': return 'PHQ-9 Depression Scale';
      case 'gad7': return 'GAD-7 Anxiety Scale';
      case 'biopsychosocial': return 'Biopsychosocial Assessment';
      case 'document_upload': return 'Supporting Documents';
      default: return 'Assessment';
    }
  };

  const renderCurrentAssessment = () => {
    if (!currentAssessment || !currentTemplate) return null;

    const props = {
      assessment: currentAssessment,
      template: currentTemplate,
      onComplete: handleAssessmentComplete,
      onError: setError
    };

    switch (currentAssessment.templateId) {
      case 'phq9':
        return <PHQ9Assessment {...props} />;
      case 'gad7':
        return <GAD7Assessment {...props} />;
      case 'biopsychosocial':
        return <BiopsychosocialAssessment {...props} />;
      case 'document_upload':
        return <DocumentUploadAssessment {...props} />;
      default:
        return (
          <Alert severity="error">
            Unknown assessment type: {currentAssessment.templateId}
          </Alert>
        );
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          {error}
          <Button onClick={() => navigate('/appointments')} sx={{ ml: 2 }}>
            Back to Appointments
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!portalData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          Assessment portal not found.
          <Button onClick={() => navigate('/appointments')} sx={{ ml: 2 }}>
            Back to Appointments
          </Button>
        </Alert>
      </Container>
    );
  }

  // All assessments complete - success view
  if (completionStatus.percentage === 100 && !currentAssessment) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Paper sx={{ p: 6 }}>
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Assessments Complete!
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Thank you for completing your pre-consultation assessments.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Your provider will review your responses before your appointment on{' '}
            <strong>{formatAppointmentDate(portalData.appointment.dateTime)}</strong>.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/appointments')}
          >
            Back to Appointments
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          FMLA Assessment Portal
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Complete your pre-consultation assessments
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Appointment: {formatAppointmentDate(portalData.appointment.dateTime)}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Assessment Progress */}
        <Grid item xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Assessment Progress</Typography>
                <Chip 
                  label={`${completionStatus.completed}/${completionStatus.total} Complete`}
                  color={completionStatus.percentage === 100 ? 'success' : 'primary'}
                />
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={completionStatus.percentage} 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {Math.round(completionStatus.percentage)}% complete
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Assessment List */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Your Assessments
              </Typography>
              <Box>
                {portalData.assessments.map((assessment) => (
                  <Box
                    key={assessment.assessmentId}
                    sx={{
                      p: 2,
                      mb: 1,
                      border: 1,
                      borderColor: currentAssessment?.assessmentId === assessment.assessmentId ? 'primary.main' : 'grey.300',
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: assessment.status === 'completed' ? 'success.50' : 'background.paper',
                      '&:hover': {
                        bgcolor: assessment.status === 'completed' ? 'success.100' : 'grey.50',
                      }
                    }}
                    onClick={() => selectAssessment(assessment.assessmentId)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '20px' }}>
                        {getAssessmentIcon(assessment.templateId)}
                      </span>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2">
                          {getAssessmentName(assessment.templateId)}
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={assessment.progress * 100} 
                          size="small"
                          sx={{ height: 4, mt: 0.5 }}
                        />
                      </Box>
                      {assessment.status === 'completed' && (
                        <CheckCircle color="success" fontSize="small" />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Current Assessment */}
        <Grid item xs={12} md={8}>
          {currentAssessment && currentTemplate ? (
            renderCurrentAssessment()
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Select an Assessment
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Choose an assessment from the list to get started.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}
    </Container>
  );
};

export default AssessmentPortalPage;